/**
 * A scrollable body with the two buttons that are the only way to move it.
 *
 * The kitchen screen does not scroll by touch. Every scrollable surface on this
 * display therefore needs explicit up/down controls, and before this component
 * there were two implementations of that rule which disagreed with each other.
 *
 * The buttons live in rails above and below the body, not floating over it: on
 * a wall screen a pill that covers the first line of the next dish is the one
 * thing worse than no button at all (FR-009). The rails keep their space while
 * the body overflows, so scrolling never makes the layout jump — the button is
 * merely not offered when there is nothing that way.
 *
 * The body can also be DRAGGED, which is what makes the gesture work on the
 * till: it reports a finger as a mouse, so a tap arrives as a click and a swipe
 * arrives as nothing at all. See `lib/drag-scroll.ts`. The buttons stay — they
 * are still the only thing that works with a glove on.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  computeScrollAffordance,
  pageScrollDelta,
  type ScrollAffordance,
} from "../lib/scroll-affordance";
import { useDragScroll } from "../hooks/useDragScroll";
import { useTranslation } from "../i18n";

interface ScrollPaneProps {
  children: React.ReactNode;
  /** Classes for the scrolling body (padding, spacing, background). */
  bodyClassName?: string;
  /** Classes for the outer column. */
  className?: string;
  /** Change this when the content changes, so the rule is re-evaluated. */
  revision?: unknown;
  /** `compact` for a card column, `roomy` for the full-height modal. */
  size?: "compact" | "roomy";
  /** What the buttons say. The kitchen reads words faster than icons. */
  labels?: { up: string; down: string };
}

const NO_OVERFLOW: ScrollAffordance = { canUp: false, canDown: false };

export default function ScrollPane({
  children,
  bodyClassName = "",
  className = "",
  revision,
  size = "compact",
  labels: labelsProp,
}: ScrollPaneProps) {
  const { t } = useTranslation();
  const labels = labelsProp ?? {
    up: t("kds.scroll.up", "Sopra"),
    down: t("kds.scroll.down", "Sotto"),
  };
  // The same element is measured, moved by the buttons, and dragged.
  const bodyRef = useDragScroll<HTMLDivElement>("y");
  const [affordance, setAffordance] = useState<ScrollAffordance>(NO_OVERFLOW);

  const measure = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    setAffordance(
      computeScrollAffordance({
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
      }),
    );
  }, []);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [measure, revision]);

  const move = (direction: "up" | "down") => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollBy({ top: pageScrollDelta(el.clientHeight, direction), behavior: "smooth" });
  };

  const overflows = affordance.canUp || affordance.canDown;
  const railHeight = size === "roomy" ? "h-16" : "h-11";
  const iconSize = size === "roomy" ? 40 : 26;
  const labelSize = size === "roomy" ? "text-kds-action" : "text-kds-meta";

  const rail = (direction: "up" | "down") => {
    if (!overflows) return null;
    const enabled = direction === "up" ? affordance.canUp : affordance.canDown;
    const Icon = direction === "up" ? ChevronUp : ChevronDown;
    return (
      <div className={`flex-shrink-0 flex items-center justify-center ${railHeight}`}>
        <button
          type="button"
          onClick={() => move(direction)}
          disabled={!enabled}
          // Space is reserved even when the button is not offered, so the layout
          // never jumps under the cook's hand mid-scroll.
          aria-hidden={!enabled}
          tabIndex={enabled ? 0 : -1}
          className={[
            "inline-flex items-center gap-2 px-6 rounded-full",
            "bg-kds-preparing text-white font-bold shadow-lg",
            "min-h-touch min-w-touch",
            labelSize,
            enabled ? "" : "invisible",
          ].join(" ")}
          aria-label={
            direction === "up"
              ? t("kds.scroll.upAria", "Scorri verso l'alto")
              : t("kds.scroll.downAria", "Scorri verso il basso")
          }
        >
          <Icon size={iconSize} strokeWidth={3} />
          {direction === "up" ? labels.up : labels.down}
        </button>
      </div>
    );
  };

  return (
    <div className={`flex flex-col min-h-0 flex-1 ${className}`}>
      {rail("up")}
      <div ref={bodyRef} className={`flex-1 min-h-0 overflow-y-auto ${bodyClassName}`}>
        {children}
      </div>
      {rail("down")}
    </div>
  );
}
